const Concept = () => {
    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8 space-y-10">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 font-mono mb-2">
                    Beyond Vocabulary & Grammar
                </h1>
                <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto"></div>
                <p className="text-gray-700 mt-4 text-lg max-w-3xl mx-auto">
                    Train pronunciation like fitness—through structured exercises, precise feedback, and repetition.
                </p>
            </div>

            {/* Core Problem - Modified with question-style statements */}
            <section className="space-y-8">
                <h2 className="text-2xl font-bold text-gray-900 font-mono border-b pb-3">
                    The Communication Gap
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-xl p-5 border-l-4 border-blue-500">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">❓</div>
                            <h3 className="text-lg font-bold text-gray-900">You know the words, but...</h3>
                        </div>
                        <div className="space-y-3">
                            <p className="text-gray-700 pl-1 font-medium">
                                • Native speakers still ask you to repeat
                            </p>
                            <p className="text-gray-700 pl-1 font-medium">
                                • Fast speech overwhelms you
                            </p>
                            <p className="text-gray-700 pl-1 font-medium">
                                •  Your perception hasn’t yet properly tuned to natural patterns and rhythms
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border-l-4 border-purple-500">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold mr-3">🎵</div>
                            <h3 className="text-lg font-bold text-gray-900">The Power of Prosody</h3>
                        </div>
                        <div className="space-y-3">
                            <p className="text-gray-700 pl-1">
                                Rhythm, stress, and intonation account for 
                                <span className="font-bold text-purple-700 ml-1">40% of understanding </span>  
                                but are often overlooked in language training.
                            </p>
                            
                        </div>
                    </div>
                </div>
            </section>

            {/* Rest of the component remains exactly the same */}
            {/* Training Approach */}
            <section className="space-y-8">
                <h2 className="text-2xl font-bold text-gray-900 font-mono border-b pb-3">
                    Our Training Approach
                </h2>
                
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-green-50 rounded-xl p-5 border-2 border-green-200">
                        <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">💪</div>
                            <h3 className="text-lg font-bold text-gray-900">Muscle Memory</h3>
                        </div>
                        <p className="text-gray-700">
                            Build vocal coordination through repetition, like gym training for your speech muscles.
                        </p>
                    </div>

                    <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200">
                        <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">🎵</div>
                            <h3 className="text-lg font-bold text-gray-900">Speech Music</h3>
                        </div>
                        <p className="text-gray-700">
                            Master rhythm, stress, and intonation—the musical elements essential for understanding.
                        </p>
                    </div>

                    <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                        <div className="flex items-center mb-4">
                            <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">🧠</div>
                            <h3 className="text-lg font-bold text-gray-900">Ear Training</h3>
                        </div>
                        <p className="text-gray-700">
                            Train your ear to recognize subtle sound differences and prosodic patterns.
                        </p>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="space-y-8">
                <h2 className="text-2xl font-bold text-gray-900 font-mono border-b pb-3">
                    How It Works
                </h2>
                
                <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-300">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="flex items-start">
                                <div className="w-10 h-10 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">👥</div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Shadowing Practice</h4>
                                    <p className="text-gray-700 text-sm">
                                        Follow human narrators to internalize natural rhythm and intonation.
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-start">
                                <div className="w-10 h-10 bg-green-500 text-white rounded-lg flex items-center justify-center font-bold mr-3">🔁</div>
                                <div>
                                    <h4 className="font-bold text-gray-900 mb-1">Smart Repetition</h4>
                                    <p className="text-gray-700 text-sm">
                                        AI identifies difficult sounds and creates optimal practice intervals.
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-5 border border-gray-200">
                            <h4 className="font-bold text-gray-900 mb-3">Learning Path</h4>
                            <div className="space-y-3">
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mr-2">1</div>
                                    <div>
                                        <h5 className="font-semibold text-gray-800 text-sm">Sound Foundations</h5>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold mr-2">2</div>
                                    <div>
                                        <h5 className="font-semibold text-gray-800 text-sm">Prosody Development</h5>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold mr-2">3</div>
                                    <div>
                                        <h5 className="font-semibold text-gray-800 text-sm">Natural Integration</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feedback Technology */}
            <section className="space-y-8">
                <h2 className="text-2xl font-bold text-gray-900 font-mono border-b pb-3">
                    Precision Feedback
                </h2>
                
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">🎯</div>
                        <h4 className="font-bold text-gray-900 mb-2">Sound Accuracy</h4>
                        <p className="text-gray-700 text-sm">
                            Real-time feedback on individual phoneme production
                        </p>
                    </div>

                    <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
                        <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">📈</div>
                        <h4 className="font-bold text-gray-900 mb-2">Prosody Analysis</h4>
                        <p className="text-gray-700 text-sm">
                            Feedback on rhythm, stress, and intonation patterns
                        </p>
                    </div>

                    <div className="bg-white rounded-xl p-5 border border-gray-200 text-center">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">🔄</div>
                        <h4 className="font-bold text-gray-900 mb-2">Adaptive Training</h4>
                        <p className="text-gray-700 text-sm">
                            Personalized progression based on your development
                        </p>
                    </div>
                </div>
            </section>

            {/* Final Statement */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 border-2 border-blue-200 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6">🎵</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                    From Knowledge to Natural Communication
                </h3>
                <p className="text-gray-700 max-w-2xl mx-auto">
                    We bridge the gap between textbook proficiency and real-world understanding by training both sounds and prosody—transforming pronunciation into a trainable skill.
                </p>
            </div>
        </div>
    );
}

export default Concept;